// src/modules/app-views/offers/pages/Init/modals/OffersParametersModal/tabs/TypesTab.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useMemo } from 'react';
import { Table, Button, Flex, Space, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import { generateUidV4 } from 'utils/helpers';
import useZustand from 'hooks/useZustand';
import TypeFormModal from './TypeFormModal';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const TypesTab = ({ loading, setLoading }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedModule = useZustand('module', 'config');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [mousePosition, setMousePosition] = useState(null);

  // Use local state for types - initialize from store, update locally after save
  const [types, setTypes] = useState(() => {
    const rawTypes = appService.getModulePreferences({ category: 'types' });
    return normalizeTypes(rawTypes);
  });

  // Filter out removed types for display in table
  const visibleTypes = useMemo(() => types.filter(t => !t.removed), [types]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleAdd = useCallback(e => {
    setMousePosition({ x: e.clientX, y: e.clientY });
    setEditingRecord(null);
    setShowModal(true);
  }, []);

  const handleEdit = useCallback((record, e) => {
    setMousePosition({ x: e.clientX, y: e.clientY });
    setEditingRecord(record);
    setShowModal(true);
  }, []);

  const handleSave = useCallback(
    async formData => {
      // Check for duplicates (excluding current item if editing, and excluding removed types)
      const isDuplicate = types.some(
        t =>
          t.uid !== editingRecord?.uid &&
          !t.removed &&
          t.name.toLowerCase() === formData.name.toLowerCase()
      );

      if (isDuplicate) {
        appService.message({ flag: 'w', text: 'item_already_exists' });
        return;
      }

      setLoading(true);
      try {
        let updatedTypes;

        if (editingRecord) {
          // Editing existing type - keep the same uid and preserve removed flag
          updatedTypes = types.map(t =>
            t.uid === editingRecord.uid
              ? { ...t, name: formData.name, description: formData.description }
              : t
          );
        } else {
          // Adding new type - generate new uid
          updatedTypes = [
            ...types,
            {
              uid: generateUidV4(),
              name: formData.name,
              description: formData.description,
            },
          ];
        }

        // Save array directly: types: [...]
        await appService.updateModulePreferences({
          category: 'types',
          data: updatedTypes,
        });

        // Update local state immediately (don't wait for SSE)
        setTypes(updatedTypes);

        appService.message({ flag: 's', text: 'saved_successfully' });
        setShowModal(false);
        setEditingRecord(null);
      } catch (error) {
        console.error('[TypesTab] Save error:', error);
        appService.message({ flag: 'e', text: 'error_saving' });
      } finally {
        setLoading(false);
      }
    },
    [types, editingRecord, setLoading]
  );

  const handleCancel = useCallback(() => {
    setShowModal(false);
    setEditingRecord(null);
  }, []);

  const handleDelete = useCallback(
    async record => {
      setLoading(true);
      try {
        const moduleId = storedModule?.id || 'offers';

        // Check if type is in use via API
        const isInUse = await appService.checkParameterInUse({
          module: moduleId,
          category: 'types',
          uid: record.uid,
        });

        let updatedTypes;

        if (isInUse) {
          // Soft delete: mark as removed but keep in array
          updatedTypes = types.map(t =>
            t.uid === record.uid ? { ...t, removed: true } : t
          );
          appService.message({ flag: 'w', text: 'item_in_use_hidden' });
        } else {
          // Hard delete: remove from array
          updatedTypes = types.filter(t => t.uid !== record.uid);
          appService.message({ flag: 's', text: 'removed_successfully' });
        }

        // Save array directly: types: [...]
        await appService.updateModulePreferences({
          category: 'types',
          data: updatedTypes,
        });

        // Update local state immediately (don't wait for SSE)
        setTypes(updatedTypes);
      } catch (error) {
        console.error('[TypesTab] Delete error:', error);
        appService.message({ flag: 'e', text: 'error_removing' });
      } finally {
        setLoading(false);
      }
    },
    [types, setLoading, storedModule?.id]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. TABLE COLUMNS
  // ─────────────────────────────────────────────────────────────────────────────
  const columns = [
    {
      title: translateX('name'),
      dataIndex: 'name',
      width: 160,
      render: text => <span className="font-weight-bold">{text}</span>,
    },
    {
      title: translateX('description'),
      dataIndex: 'description',
      render: text => text || <span className="text-muted">-</span>,
    },
    {
      title: translateX('actions'),
      dataIndex: 'actions',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={e => handleEdit(record, e)}
            disabled={loading}
          />
          <Popconfirm
            title={translateX('confirm_delete')}
            onConfirm={() => handleDelete(record)}
            okText={translateX('yes')}
            cancelText={translateX('no')}
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} disabled={loading} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Flex vertical style={{ height: '100%' }}>
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Table
          rowKey="uid"
          columns={columns}
          dataSource={visibleTypes}
          pagination={false}
          size="small"
          loading={loading}
          locale={{ emptyText: translateX('no_data') }}
          scroll={{ y: 'calc(80vh - 240px)' }}
        />
      </div>

      <Flex justify="flex-end" className="mt-3" style={{ flexShrink: 0 }}>
        <Button icon={<PlusOutlined />} onClick={handleAdd} disabled={loading}>
          {translateX('add_type')}
        </Button>
      </Flex>

      <TypeFormModal
        open={showModal}
        onCancel={handleCancel}
        onSave={handleSave}
        initialData={editingRecord}
        loading={loading}
        mousePosition={mousePosition}
      />
    </Flex>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default TypesTab;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function normalizeTypes(rawData) {
  if (!rawData || !Array.isArray(rawData)) return [];
  return rawData.map(normalizeType);
}

function normalizeType(type) {
  return {
    uid: type.uid,
    name: type.name || '',
    description: type.description || '',
    ...(type.removed && { removed: true }),
  };
}
